import { Link } from 'react-router-dom'
import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, dateTimeLabel, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface Base {
  _id: string
  billNumber: string
  time: string | null
  staff: string
}
interface Removed extends Base {
  editedBy: string
  editedAt: string | null
  item: string
  removedQty: number
  amount: number
  reason: string
}
interface Override extends Base {
  item: string
  basePrice: number
  price: number
  difference: number
}
interface Cancelled extends Base {
  total: number
  table: string
}
interface Gap {
  from: number
  to: number
  count: number
}
interface AuditData {
  removedItems: Removed[]
  priceOverrides: Override[]
  cancelledBills: Cancelled[]
  gaps: Gap[]
  summary: { editedBills: number; removedItems: number; priceOverrides: number; cancelledBills: number; missingNumbers: number }
}

export default function AuditTrailScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<AuditData>('audit-trail')
  const data = report.data

  const removedColumns: Column<Removed>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'editedAt', header: 'Removed at', render: r => dateTimeLabel(r.editedAt) },
    { key: 'item', header: 'Item', render: r => `${r.item} × ${r.removedQty}` },
    { key: 'editedBy', header: 'By' },
    { key: 'reason', header: 'Reason', render: r => r.reason || '—' },
    { key: 'amount', header: 'Value', align: 'right', render: r => formatCurrency(r.amount) }
  ]
  const overrideColumns: Column<Override>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'time', header: 'Time', render: r => dateTimeLabel(r.time) },
    { key: 'item', header: 'Item' },
    { key: 'staff', header: 'Staff' },
    { key: 'basePrice', header: 'Menu price', align: 'right', render: r => formatCurrency(r.basePrice) },
    { key: 'price', header: 'Billed at', align: 'right', render: r => formatCurrency(r.price) },
    {
      key: 'difference',
      header: 'Difference',
      align: 'right',
      render: r => <span className={r.difference < 0 ? 'font-semibold text-red-500 dark:text-red-400' : 'font-semibold text-green-600 dark:text-green-400'}>{formatCurrency(r.difference)}</span>
    }
  ]
  const cancelledColumns: Column<Cancelled>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'time', header: 'Time', render: r => dateTimeLabel(r.time) },
    { key: 'table', header: 'Table' },
    { key: 'staff', header: 'Staff' },
    { key: 'total', header: 'Amount', align: 'right', render: r => formatCurrency(r.total) }
  ]
  const gapColumns: Column<Gap>[] = [
    { key: 'from', header: 'Missing from', render: r => `#${r.from}` },
    { key: 'to', header: 'Missing to', render: r => `#${r.to}` },
    { key: 'count', header: 'Bills missing', align: 'right' }
  ]

  return (
    <ReportPage
      eyebrow="Audit · Fraud watch"
      title="Audit Trail"
      subtitle="Removed items, price overrides, cancelled bills and gaps in bill numbering"
      report={report}
      onExport={() => exportCsv('audit-removed-items', removedColumns, data?.removedItems ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Edited bills', value: data.summary.editedBills, sub: 'See the Edited Bills page for details' },
              { label: 'Items removed after billing', value: data.summary.removedItems, tone: data.summary.removedItems > 0 ? 'warn' : undefined },
              { label: 'Price overrides', value: data.summary.priceOverrides, tone: data.summary.priceOverrides > 0 ? 'warn' : undefined },
              { label: 'Missing bill numbers', value: data.summary.missingNumbers, sub: `${data.summary.cancelledBills} cancelled bills`, tone: data.summary.missingNumbers > 0 ? 'bad' : undefined }
            ]}
          />
          <p className="text-sm text-muted-foreground">
            Every edit with who and why is on the{' '}
            <Link to="/edited-bills" className="font-medium text-foreground underline underline-offset-2">
              Edited Bills
            </Link>{' '}
            page.
          </p>
          <DataTable title="Items removed after billing" columns={removedColumns} rows={data.removedItems} rowKey={(r, i) => `${r._id}-${i}`} defaultSort="editedAt" csvName="removed-items" empty="No items were removed after billing." />
          <DataTable title="Price overrides" hint="Billed at a price different from the menu price" columns={overrideColumns} rows={data.priceOverrides} rowKey={(r, i) => `${r._id}-${i}`} defaultSort="difference" csvName="price-overrides" empty="No price overrides found." />
          <DataTable title="Cancelled bills" columns={cancelledColumns} rows={data.cancelledBills} rowKey={r => r._id} defaultSort="time" empty="No cancelled bills." />
          <DataTable title="Gaps in bill numbering" hint="Numbers missing between the first and last bill of the period" columns={gapColumns} rows={data.gaps} rowKey={r => String(r.from)} defaultSort="from" empty="No gaps — bill numbers are continuous." />
        </>
      )}
    </ReportPage>
  )
}
